import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { Router } from './router.tsx';
import { startSwUpdateWatch } from './lib/sw-update.ts';
import './styles.css';
import '@fontsource/geist-sans/300.css';
import '@fontsource/geist-sans/400.css';
import '@fontsource/geist-sans/500.css';
import '@fontsource/geist-sans/600.css';
import '@fontsource/geist-sans/700.css';
import '@fontsource/geist-mono/400.css';
import '@fontsource/geist-mono/500.css';
import '@fontsource/newsreader/400.css';
import '@fontsource/newsreader/500.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry: 1,
      // Treat data as fresh for 60s — tab switches reuse the cached copy
      // immediately instead of remounting into a loading state.
      staleTime: 60_000,
      // While a refetch is in flight after navigating back, keep showing the
      // previous data. The view never flashes empty just because the query
      // key invalidates.
      placeholderData: (prev: unknown) => prev,
    },
  },
});

// Service worker auto-update: precaches in background, prompts to reload.
if (import.meta.env.PROD) startSwUpdateWatch();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Router />
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: 'var(--bg-elevated)',
            color: 'var(--text-1)',
            border: '1px solid var(--border)',
            fontFamily: 'Geist Sans, system-ui, sans-serif',
          },
        }}
      />
    </QueryClientProvider>
  </StrictMode>,
);
